import { FocusScope } from '@react-aria/focus';
import { useCallback, useState, type FunctionComponent } from 'react';
import Media from 'react-media';

import { Analytics } from '@trello/atlassian-analytics';
import { useOneTimeMessagesDismissed } from '@trello/business-logic-react/member';
import { ScreenBreakpoints } from '@trello/media-queries';
import { Overlay } from '@trello/nachos/overlay';
import { useSplitScreenSharedState } from '@trello/split-screen';

import { splitScreenOnboardingDismissalState } from 'app/src/components/SplitScreenManager/splitScreenOnboardingDismissalState';
import { CarouselScreen } from './CarouselScreen';
import { CloseButton } from './CloseButton';
import { LandingScreen } from './LandingScreen';
import { SkipButton } from './SkipButton';
import { VideoScreen } from './VideoScreen';

import styles from './SplitScreenOnboarding.less';

export type OnboardingScreen = 'carousel' | 'end' | 'landing' | 'video';

const LandingScreenWithoutAnimation: FunctionComponent<{
  advanceToEndScreen: () => void;
  setOnboardingScreen: (screen: OnboardingScreen) => void;
}> = ({ advanceToEndScreen, setOnboardingScreen }) => (
  <LandingScreen
    onDiscoveryButtonClick={() => advanceToEndScreen()}
    onVideoButtonClick={() => setOnboardingScreen('video')}
  />
);

const LandingScreenWithAnimation: FunctionComponent<{
  setOnboardingScreen: (screen: OnboardingScreen) => void;
}> = ({ setOnboardingScreen }) => (
  <LandingScreen
    onDiscoveryButtonClick={() => setOnboardingScreen('carousel')}
    onVideoButtonClick={() => setOnboardingScreen('video')}
  />
);

export const SplitScreenOnboarding: FunctionComponent<{
  onComplete: () => void;
}> = ({ onComplete }) => {
  const [onboardingScreen, setOnboardingScreen] =
    useState<OnboardingScreen>('landing');

  const [skipToFinalState, setSkipTofinalState] = useState(false);
  const { dismissOneTimeMessage } = useOneTimeMessagesDismissed();
  const { toggleInbox, panels } = useSplitScreenSharedState();

  const advanceToEndScreen = useCallback(() => {
    setSkipTofinalState(true);
    setOnboardingScreen('end');
  }, [setSkipTofinalState]);

  const shouldShowSkipButton = ['carousel', 'landing'].includes(
    onboardingScreen,
  );

  const shouldShowCloseButton = ['end', 'video'].includes(onboardingScreen);

  const onCloseFullScreen = useCallback(() => {
    if (!panels.inbox) {
      toggleInbox();
    }
    dismissOneTimeMessage('split-screen-onboarding');
    splitScreenOnboardingDismissalState.setValue({
      timestamp: Date.now(),
    });
    onComplete();

    Analytics.sendTrackEvent({
      action: 'completed',
      actionSubject: 'onboarding',
      actionSubjectId: 'personalProductivitySplitScreenOnboarding',
      source: 'splitScreenOnboardingEndScreen',
    });
  }, [dismissOneTimeMessage, panels.inbox, toggleInbox, onComplete]);

  const mediaQueries = {
    prefersReducedMotion: { 'prefers-reduced-motion': 'reduce' },
    small: ScreenBreakpoints.Small,
  };

  return (
    <Overlay
      className={styles.overlayOverrides}
      closeOnEscape={false}
      onClose={() => {}}
    >
      <FocusScope contain restoreFocus autoFocus>
        <div className={styles.overlay}>
          {shouldShowSkipButton && (
            <SkipButton
              onSkipButtonClick={advanceToEndScreen}
              screen={onboardingScreen}
            />
          )}
          {shouldShowCloseButton && (
            <CloseButton
              onCloseButtonClick={
                onboardingScreen === 'video'
                  ? () => setOnboardingScreen('landing')
                  : onCloseFullScreen
              }
              screen={onboardingScreen}
            />
          )}
          <div className={styles.screenWrapper}>
            {onboardingScreen === 'landing' && (
              <Media queries={mediaQueries}>
                {(matches) => (
                  <>
                    {matches.small || matches.prefersReducedMotion ? (
                      <LandingScreenWithoutAnimation
                        advanceToEndScreen={advanceToEndScreen}
                        setOnboardingScreen={setOnboardingScreen}
                      />
                    ) : (
                      <LandingScreenWithAnimation
                        setOnboardingScreen={setOnboardingScreen}
                      />
                    )}
                  </>
                )}
              </Media>
            )}
            {['carousel', 'end'].includes(onboardingScreen) && (
              <Media queries={mediaQueries}>
                {(matches) => (
                  <CarouselScreen
                    onInboxNavigationClick={onCloseFullScreen}
                    skipToFinalState={
                      skipToFinalState ||
                      matches.small ||
                      matches.prefersReducedMotion
                    }
                    onAnimationEnd={() => {
                      setOnboardingScreen('end');
                    }}
                  />
                )}
              </Media>
            )}
            {onboardingScreen === 'video' && <VideoScreen />}
          </div>
        </div>
      </FocusScope>
    </Overlay>
  );
};
